import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
    FileText, Search, Download, Settings, Mail,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import EmailDocumentDialog from './EmailDocumentDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBytes = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Compliance / Technical / NHS Integration documents tab.
 * Self-contained: handles its own download / preview / email actions.
 *
 * Receives from parent only the things that already exist there:
 *   - authToken (for axios Authorization header)
 *   - docCategories, docsLoading (already fetched at parent for caching)
 *   - previewDocument(category, filename, title) callback (parent shows the preview dialog)
 *   - onConfigureSignatories() callback (parent shows the signatories dialog)
 */
export default function ComplianceDocumentsTab({
    authToken,
    docCategories,
    docsLoading,
    previewDocument,
    onConfigureSignatories,
}) {
    const headers = { Authorization: `Bearer ${authToken}` };
    const [emailDialog, setEmailDialog] = useState({ open: false, doc: null });

    const downloadDocument = async (category, filename) => {
        try {
            const response = await axios.get(
                `${API}/admin/compliance-documents/${category}/${filename}`,
                { headers, responseType: 'blob' },
            );
            const blob = new Blob([response.data], { type: 'text/markdown;charset=utf-8' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${filename}`);
        } catch (e) {
            console.error('Download error:', e);
            toast.error('Failed to download document');
        }
    };

    const downloadDocumentPdf = async (category, filename) => {
        const t = toast.loading('Generating PDF...');
        try {
            const response = await axios.get(
                `${API}/admin/compliance-documents/${category}/${filename}/pdf`,
                { headers, responseType: 'blob' },
            );
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename.replace(/\.md$/, '.pdf');
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded ${a.download}`, { id: t });
        } catch (e) {
            console.error('PDF download error:', e);
            toast.error('Failed to generate PDF', { id: t });
        }
    };

    const downloadZip = async (format) => {
        const t = format === 'pdf' ? toast.loading('Generating PDFs...') : null;
        try {
            const response = await axios.get(
                `${API}/admin/compliance-documents-zip?format=${format}`,
                { headers, responseType: 'blob' },
            );
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = format === 'pdf'
                ? 'FendanaCor_Compliance_PDFs.zip'
                : 'FendanaCor_Compliance_Markdown.zip';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Downloaded all as ${format.toUpperCase()} ZIP`, t ? { id: t } : undefined);
        } catch (e) {
            toast.error(`Failed to generate ${format.toUpperCase()} ZIP`, t ? { id: t } : undefined);
        }
    };

    const openEmailDialog = (cat, file) => {
        setEmailDialog({
            open: true,
            doc: { category: cat.category, filename: file.filename, title: file.title },
        });
    };

    return (
        <>
            <Card data-testid="documents-card">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-teal-600" />
                        Compliance &amp; Technical Documents
                    </CardTitle>
                    <CardDescription>
                        Download, preview or email Technical documentation, NHS Integration / DPIA artifacts and ISMS policies.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <Button
                            variant="outline"
                            onClick={onConfigureSignatories}
                            data-testid="configure-signatories-btn"
                            className="w-full sm:w-auto"
                        >
                            <Settings className="w-4 h-4 mr-2" />
                            Configure Sign-off Names
                        </Button>
                        <div className="flex flex-col sm:flex-row gap-2">
                            <Button
                                variant="outline"
                                onClick={() => downloadZip('md')}
                                data-testid="download-all-md-zip"
                                className="w-full sm:w-auto"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                All as Markdown (ZIP)
                            </Button>
                            <Button
                                onClick={() => downloadZip('pdf')}
                                data-testid="download-all-pdf-zip"
                                className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto"
                            >
                                <Download className="w-4 h-4 mr-2" />
                                All as Branded PDFs (ZIP)
                            </Button>
                        </div>
                    </div>

                    {docsLoading && (
                        <p className="text-sm text-slate-500">Loading documents...</p>
                    )}
                    {!docsLoading && docCategories.length === 0 && (
                        <p className="text-sm text-slate-500">No documents available.</p>
                    )}

                    {docCategories.map((cat) => (
                        <div key={cat.category} data-testid={`doc-category-${cat.category}`}>
                            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                                {cat.label}
                            </h3>
                            <div className="space-y-2">
                                {cat.files.map((file) => (
                                    <div
                                        key={file.filename}
                                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                                        data-testid={`doc-row-${file.filename}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                                                    {file.title}
                                                </p>
                                                <p className="text-xs text-slate-500 truncate">
                                                    {file.filename} · {formatBytes(file.size_bytes)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => previewDocument(cat.category, file.filename, file.title)}
                                                data-testid={`preview-${file.filename}`}
                                            >
                                                <Search className="w-3.5 h-3.5 mr-1.5" />
                                                Preview
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => downloadDocument(cat.category, file.filename)}
                                                data-testid={`download-md-${file.filename}`}
                                            >
                                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                                .md
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => downloadDocumentPdf(cat.category, file.filename)}
                                                data-testid={`download-pdf-${file.filename}`}
                                                className="bg-teal-600 hover:bg-teal-700"
                                            >
                                                <Download className="w-3.5 h-3.5 mr-1.5" />
                                                PDF
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openEmailDialog(cat, file)}
                                                data-testid={`email-${file.filename}`}
                                                className="border-teal-300 text-teal-700 hover:bg-teal-50"
                                            >
                                                <Mail className="w-3.5 h-3.5 mr-1.5" />
                                                Email
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>

            <EmailDocumentDialog
                open={emailDialog.open}
                onOpenChange={(open) => setEmailDialog({ ...emailDialog, open })}
                authToken={authToken}
                doc={emailDialog.doc}
            />
        </>
    );
}
